import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControlLabel,
    Checkbox
    //Typography
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import LayoutAdmin from "../components/LayoutAdmin";
import axiosExt from '../axios';
import { DetailState } from "../types/index";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

const DetailStatePage: React.FC = () => {
    const { t } = useTranslation();

    const [items, setItems] = useState<DetailState[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<DetailState>({
        id:null,
        name: "",
        sortOrder:null,
        isClosed: false,
        defaultForNew: false,
        colorRGB: "#000000",
        bgColorRGB: "#ffffff",
    });

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const savedSortKey = "sortDetailStatesList";
    const savedFilterKey = "filterDetailStatesList";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchItems();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchItems = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/DetailStates`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setItems(response.data);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        //const payload = { name, colorRGB };

        if (selectedItem.id) {
            await axiosExt.put(`${API_BASE_URL}/DetailStates/${selectedItem.id}`, selectedItem, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } else {
            selectedItem.id = crypto.randomUUID();
            await axiosExt.post(`${API_BASE_URL}/DetailStates`, selectedItem, {
                headers: { Authorization: `Bearer ${token}` },
            });
        }

        setOpen(false);
        fetchItems();
    };

    /*const handleDelete = async (id: number) => {
        const token = localStorage.getItem("token");
        await axiosExt.delete(`${API_BASE_URL}/DetailStates/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchItems();
    };*/

    const handleDelete = async (id: string) => {
        //const token = localStorage.getItem("token");

        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/DetailStates/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchItems();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };


    const handleEdit = (item: DetailState) => {
        setSelectedItem(item);
        setOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Name"), width: 150 },
        { field: "sortOrder", headerName: t("Sort Order"), width: 100 },
        {
            field: "defaultForNew", headerName: t("Default For New"), width: 100,
            valueFormatter: (params) => {
                if (params == null) {
                    return "no";
                }
                else {
                    if (params == true) { return t("yes"); }
                    else { return t("no"); }
                }
            }
        },
        {
            field: "isClosed", headerName: t("Is Closed"), width: 100,
            valueFormatter: (params) => {
                if (params == null) {
                    return t("no");
                }
                else {
                    if (params == true) { return t("yes"); }
                    else { return t("no"); }
                }
            }
        },
        { field: "colorRGB", headerName: t("Color"), width: 150 },
        { field: "bgColorRGB", headerName: t("Background Color"), width: 150 },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 250,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)}>{t("Edit")}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        style={{ marginLeft: 10 }}
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <div style={{ height: "100%" }}>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2>{t("Detail States")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedItem({
                                id: null,
                                name: "",
                                sortOrder:null,
                                isClosed: false,
                                defaultForNew: false,
                                colorRGB: "#000000",
                                bgColorRGB: "#ffffff",
                            });
                            setOpen(true);
                        }}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                    >
                        {t("Add New")}
                    </Button>
                </div>
                <Box sx={{ width: "100%" }}>
                    <DataGrid rows={items} columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100, 200]}
                    />
                </Box>

                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{selectedItem ? "Edit Detail State" : "Add Detail State"}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={t("Name")}
                            fullWidth
                            value={selectedItem?.name || ""}
                            onChange={(e) => setSelectedItem((prev) =>
                                prev
                                    ? { ...prev, name: e.target.value }
                                    : { name: e.target.value, sortOrder: null, id: "", colorRGB: "", bgColorRGB: "", isClosed:false, defaultForNew: false, isMerged: false, allowMergeInto: false } // Provide a valid default object
                            )}
                            margin="dense"
                        />
                        <TextField
                            label={t("Sort Order")}
                            type="number"
                            fullWidth
                            value={selectedItem?.sortOrder ?? ""}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev
                                        ? { ...prev, sortOrder: e.target.value === "" ? null : Number(e.target.value) }
                                        : { name: "", sortOrder: e.target.value === "" ? null : Number(e.target.value), id: "", colorRGB: "", bgColorRGB: "", isClosed: false, defaultForNew: false, isMerged: false, allowMergeInto: false } // Provide a valid default object

                                )
                            }
                            margin="dense"
                        />
                    </DialogContent>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedItem?.defaultForNew || false}
                                onChange={(e) => setSelectedItem((prev) =>
                                    prev
                                        ? { ...prev, defaultForNew: e.target.checked }
                                        : { name: "", sortOrder: null, id: "", colorRGB: "", bgColorRGB: "", isClosed:false, defaultForNew: e.target.checked, isMerged: false, allowMergeInto: false } // Provide a valid default object
                                )}
                                color="primary"
                            />
                        }
                        label={t("Default For New")}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedItem?.isClosed || false}
                                onChange={(e) => setSelectedItem((prev) =>
                                    prev
                                        ? { ...prev, isClosed: e.target.checked }
                                        : { name: "", sortOrder: null, id: "", colorRGB: "", bgColorRGB: "", isClosed: false, defaultForNew: e.target.checked, isMerged: false, allowMergeInto: false } // Provide a valid default object
                                )}
                                color="primary"
                            />
                        }
                        label={t("Is Closed")}
                    />
                    <DialogContent>
                        <TextField
                            label={t("Color")}
                            name="color"
                            value={selectedItem?.colorRGB || "#ffffff"}
                            type="color"
                            onChange={(e) => setSelectedItem((prev) => ({
                                ...(prev ?? { id: "", sortOrder: null, name: "", bgColorRGB: "#ffffff", colorRGB: "0", defaultForNew: false }),
                                colorRGB: e.target.value
                            }))}
                            sx={{ width: 100, mr: 2 }}
                        />

                        <span
                            style={{
                                width: 20,
                                height: 20,
                                border: '1px solid #000',
                                display: 'inline-block', // Ensures proper box rendering
                                backgroundColor: selectedItem?.colorRGB, // Set the background color to the selected color
                            }}
                        />
                    </DialogContent>        
                    <DialogContent>
                        <TextField
                            label={t("Background Color")}
                            name="color"
                            value={selectedItem?.bgColorRGB || "#ffffff"}
                            type="color"
                            onChange={(e) => setSelectedItem((prev) => ({
                                ...(prev ?? { id: "", name: "", sortOrder: null, bgColorRGB: "#ffffff", colorRGB: "0", defaultForNew: false }),
                                bgColorRGB: e.target.value
                            }))}
                            sx={{ width: 100, mr: 2 }}
                        />

                        <span
                            style={{
                                width: 20,
                                height: 20,
                                border: '1px solid #000',
                                display: 'inline-block', // Ensures proper box rendering
                                backgroundColor: selectedItem?.bgColorRGB, // Set the background color to the selected color
                            }}
                        />
                    </DialogContent>        
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                        <Button onClick={handleSave}>{t("Save")}</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </LayoutAdmin>
    );
};

export default DetailStatePage;
